'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { easeInOutCubic, interpolateColor } from '@/lib/features/animatedBackground/agentThemes'
import {
  clearBlobStates,
  completeTransition,
  initializeWithCookieState,
  saveBlobStates,
  updateBlobStates,
  updateColorsForTransition,
} from '@/lib/features/animatedBackground/animatedBackgroundSlice'
import {
  type InfiniteBlobSplineSystem,
  type SplineBlob,
  createInfiniteBlobSplineSystem,
  createSplineBlob,
  getBlobPositionInfinite,
  updateBlobProgressInfinite,
} from '@/lib/features/animatedBackground/animationPaths'
import {
  getAnimatedBackgroundCookieState,
  getElapsedTime,
  initializeAnimatedBackgroundCookieState,
  isCookieStateValid,
} from '@/lib/features/animatedBackground/cookieState'
import { animatedBackgroundDebugState } from '@/lib/features/animatedBackground/debugState'
import type { RootState } from '@/lib/store'
import { useAnimatedBackgroundOverride } from './AnimatedBackgroundOverride'

type Blob = SplineBlob

interface AnimatedBackgroundWebGLProps {
  className?: string
  debugMode?: boolean
}

const vertexShaderSource = `
  attribute vec2 a_position;

  uniform vec2 u_resolution;

  varying vec2 v_worldPos;
  varying vec2 v_texCoord;

  void main() {
    gl_Position = vec4(a_position, 0, 1);

    v_worldPos = ((a_position + 1.0) * 0.5) * u_resolution;

    v_texCoord = (a_position + 1.0) * 0.5;
  }
`

const fragmentShaderSource = `
  precision mediump float;

  varying vec2 v_worldPos;

  uniform int u_blobCount;
  uniform vec2 u_blobPositions[10];
  uniform float u_blobSizes[10];
  uniform vec3 u_blobColors[10];
  uniform float u_blobOpacities[10];

  void main() {
    float field = 0.0;
    vec3 blendedColor = vec3(0.0);
    float totalInfluence = 0.0;

    for (int i = 0; i < 10; i++) {
      if (i >= u_blobCount) break;

      vec2 blobPos = u_blobPositions[i];
      float blobSize = u_blobSizes[i];
      vec3 blobColor = u_blobColors[i];
      float blobOpacity = u_blobOpacities[i];

      float dist = distance(v_worldPos, blobPos);

      float radius = blobSize * 0.5;
      float influence = (radius * radius) / (dist * dist + 1.0);

      field += influence;

      float colorWeight = influence * blobOpacity;
      blendedColor += blobColor * colorWeight;
      totalInfluence += colorWeight;
    }

    if (totalInfluence > 0.0) {
      blendedColor /= totalInfluence;
    }

    float threshold = 0.5;

    if (field < threshold) {
      discard;
    }

    float alpha = min(0.8, (field - threshold) * 2.0);
    alpha = smoothstep(0.0, 0.8, alpha);

    gl_FragColor = vec4(blendedColor, alpha);
  }
`

const blurVertexShaderSource = `
  attribute vec2 a_position;
  varying vec2 v_texCoord;

  void main() {
    gl_Position = vec4(a_position, 0, 1);
    v_texCoord = (a_position + 1.0) * 0.5;
  }
`

const blurFragmentShaderSource = `
  precision mediump float;

  varying vec2 v_texCoord;
  uniform sampler2D u_texture;
  uniform vec2 u_resolution;
  uniform vec2 u_direction;
  uniform float u_blurRadius;

  void main() {
    vec2 texelSize = 1.0 / u_resolution;
    vec4 color = vec4(0.0);
    float totalWeight = 0.0;

    float radius = u_blurRadius;

    const int MAX_SAMPLES = 192;

    for (int i = 0; i <= MAX_SAMPLES; i++) {
      float fi = float(i);

      if (fi > radius) {
        break;
      }

      float weight = exp(-(fi * fi) / (2.0 * radius * radius / 9.0));
      vec2 offset = u_direction * fi * texelSize * 3.0;

      if (fi == 0.0) {
        color += texture2D(u_texture, v_texCoord) * weight;
        totalWeight += weight;
      } else {
        vec4 positiveSample = texture2D(u_texture, v_texCoord + offset);
        vec4 negativeSample = texture2D(u_texture, v_texCoord - offset);
        color += positiveSample * weight;
        color += negativeSample * weight;
        totalWeight += weight * 2.0;
      }
    }

    gl_FragColor = color / totalWeight;
  }
`

export function AnimatedBackgroundWebGL({
  className = '',
  debugMode = false,
}: AnimatedBackgroundWebGLProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const glRef = useRef<WebGLRenderingContext | null>(null)
  const programRef = useRef<WebGLProgram | null>(null)
  const blurProgramRef = useRef<WebGLProgram | null>(null)
  const blobsRef = useRef<Blob[]>([])
  const splineSystemsRef = useRef<Map<string, InfiniteBlobSplineSystem>>(new Map())
  const currentDimensionsRef = useRef({ width: 0, height: 0 })
  const scaleFactorRef = useRef(1)
  const blurScaleRef = useRef(0.5)
  const animationTimeRef = useRef({ lastTime: 0, frameCount: 0 })
  const currentBlurRef = useRef(64.0)
  const targetBlurRef = useRef(64.0)
  const bufferRef = useRef<{
    position: WebGLBuffer | null
    center: WebGLBuffer | null
    size: WebGLBuffer | null
    color: WebGLBuffer | null
    indices: WebGLBuffer | null
  }>({
    position: null,
    center: null,
    size: null,
    color: null,
    indices: null,
  })

  const framebufferRef = useRef<{
    framebuffer1: WebGLFramebuffer | null
    framebuffer2: WebGLFramebuffer | null
    texture1: WebGLTexture | null
    texture2: WebGLTexture | null
    width: number
    height: number
    scale: number
  }>({
    framebuffer1: null,
    framebuffer2: null,
    texture1: null,
    texture2: null,
    width: 0,
    height: 0,
    scale: 0.5,
  })

  const dispatch = useDispatch()
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [webglSupported, setWebglSupported] = useState(true)
  const { override } = useAnimatedBackgroundOverride()

  const {
    isEnabled,
    speed: reduxSpeed,
    colors: reduxColors,
    performanceMode,
    savedBlobStates,
    lastDimensions,
    targetColors: reduxTargetColors,
    isTransitioning,
  } = useSelector((state: RootState) => state.animatedBackground)

  // Apply overrides if present (for tab-specific changes)
  const speed = override.speed ?? reduxSpeed
  const colors = override.colors ?? reduxColors
  const targetColors = override.colors ?? reduxTargetColors

  const transitionStartTimeRef = useRef<number | null>(null)
  const transitionStartColorsRef = useRef<string[]>([])

  // Refs for transition state (to avoid stale closure in animate callback)
  const isTransitioningRef = useRef(false)
  const colorsRef = useRef<typeof colors>([])
  const targetColorsRef = useRef<typeof targetColors>([])

  const createShader = useCallback(
    (gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null => {
      const shader = gl.createShader(type)
      if (!shader) return null

      gl.shaderSource(shader, source)
      gl.compileShader(shader)

      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader)
        return null
      }

      return shader
    },
    []
  )

  const createProgram = useCallback(
    (
      gl: WebGLRenderingContext,
      vertexShader: WebGLShader,
      fragmentShader: WebGLShader
    ): WebGLProgram | null => {
      const program = gl.createProgram()
      if (!program) return null

      gl.attachShader(program, vertexShader)
      gl.attachShader(program, fragmentShader)
      gl.linkProgram(program)

      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        gl.deleteProgram(program)
        return null
      }

      return program
    },
    []
  )

  // Initialize WebGL
  const initWebGL = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return false

    const gl =
      canvas.getContext('webgl') ||
      (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null)
    if (!gl) {
      setWebglSupported(false)
      return false
    }

    glRef.current = gl

    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource)
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource)

    if (!vertexShader || !fragmentShader) {
      setWebglSupported(false)
      return false
    }

    const program = createProgram(gl, vertexShader, fragmentShader)
    if (!program) {
      setWebglSupported(false)
      return false
    }

    const blurVertexShader = createShader(gl, gl.VERTEX_SHADER, blurVertexShaderSource)
    const blurFragmentShader = createShader(gl, gl.FRAGMENT_SHADER, blurFragmentShaderSource)

    if (!blurVertexShader || !blurFragmentShader) {
      setWebglSupported(false)
      return false
    }

    const blurProgram = createProgram(gl, blurVertexShader, blurFragmentShader)
    if (!blurProgram) {
      setWebglSupported(false)
      return false
    }

    programRef.current = program
    blurProgramRef.current = blurProgram

    bufferRef.current = {
      position: gl.createBuffer(),
      center: gl.createBuffer(),
      size: gl.createBuffer(),
      color: gl.createBuffer(),
      indices: gl.createBuffer(),
    }

    const positions = new Float32Array([-1, -1, 1, -1, 1, 1, -1, 1])

    const indices = new Uint16Array([0, 1, 2, 0, 2, 3])

    gl.bindBuffer(gl.ARRAY_BUFFER, bufferRef.current.position)
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW)

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, bufferRef.current.indices)
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW)

    gl.enable(gl.BLEND)
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)

    return true
  }, [createShader, createProgram])

  // Create or resize framebuffers for blur passes
  const createFramebuffers = useCallback(
    (gl: WebGLRenderingContext, width: number, height: number, scale: number) => {
      const scaledWidth = Math.max(1, Math.floor(width * scale))
      const scaledHeight = Math.max(1, Math.floor(height * scale))

      if (
        framebufferRef.current.width === scaledWidth &&
        framebufferRef.current.height === scaledHeight
      ) {
        framebufferRef.current.scale = scale
        return
      }

      const fb = framebufferRef.current
      if (fb.framebuffer1) gl.deleteFramebuffer(fb.framebuffer1)
      if (fb.framebuffer2) gl.deleteFramebuffer(fb.framebuffer2)
      if (fb.texture1) gl.deleteTexture(fb.texture1)
      if (fb.texture2) gl.deleteTexture(fb.texture2)

      const framebuffer1 = gl.createFramebuffer()
      const texture1 = gl.createTexture()

      gl.bindTexture(gl.TEXTURE_2D, texture1)
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        scaledWidth,
        scaledHeight,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        null
      )
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)

      gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer1)
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture1, 0)

      const framebuffer2 = gl.createFramebuffer()
      const texture2 = gl.createTexture()

      gl.bindTexture(gl.TEXTURE_2D, texture2)
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        scaledWidth,
        scaledHeight,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        null
      )
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)

      gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer2)
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture2, 0)

      gl.bindFramebuffer(gl.FRAMEBUFFER, null)

      framebufferRef.current = {
        framebuffer1,
        framebuffer2,
        texture1,
        texture2,
        width: scaledWidth,
        height: scaledHeight,
        scale,
      }
    },
    []
  )

  const getBlurScale = useCallback((mode: string): number => {
    switch (mode) {
      case 'high':
        return 0.45
      case 'medium':
        return 0.35
      case 'low':
        return 0.3
      default:
        return 0.35
    }
  }, [])

  // Get blur amount based on performance mode (extreme blur for glassy effect)
  // Base values are calibrated for 900px reference dimension (1440p height at devicePixelRatio scaling)
  const getBlurAmount = useCallback((mode: string): number => {
    const baseBlur = (() => {
      switch (mode) {
        case 'high':
          return 72.0
        case 'medium':
          return 64.0
        case 'low':
          return 21.0 // 16 * (1440/1080) ≈ 21
        default:
          return 64.0
      }
    })()

    return baseBlur * scaleFactorRef.current
  }, [])

  // Update target blur when performance mode or dimensions change
  useEffect(() => {
    blurScaleRef.current = getBlurScale(performanceMode)
    targetBlurRef.current = getBlurAmount(performanceMode)
  }, [performanceMode, getBlurAmount, getBlurScale, dimensions])

  // Sync Redux state to refs for animate callback
  useEffect(() => {
    isTransitioningRef.current = isTransitioning
    colorsRef.current = colors
    targetColorsRef.current = targetColors

    if (isTransitioning) {
      console.log('[AnimatedBackgroundWebGL] Transition state updated in refs:', {
        isTransitioning,
        colors: colors.map((c) => c.color),
        targetColors: targetColors.map((c) => c.color),
      })
    }
  }, [isTransitioning, colors, targetColors])

  const hexToRgb = useCallback((hex: string): [number, number, number] => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? [
          parseInt(result[1], 16) / 255,
          parseInt(result[2], 16) / 255,
          parseInt(result[3], 16) / 255,
        ]
      : [1, 1, 1]
  }, [])

  useEffect(() => {
    initializeAnimatedBackgroundCookieState()
  }, [])

  const hasRestoredRef = useRef<boolean>(false)
  useEffect(() => {
    if (hasRestoredRef.current || savedBlobStates.length === 0 || blobsRef.current.length > 0) {
      return
    }
    hasRestoredRef.current = true

    if (savedBlobStates.length > 0) {
      const isCorrupted = savedBlobStates.some(
        (blob) => blob.size === undefined || blob.size === null || isNaN(blob.size)
      )

      if (isCorrupted) {
        dispatch(clearBlobStates())
        return
      }

      blobsRef.current = savedBlobStates.map((savedBlob) => {
        let speedProgress = savedBlob.speedProgress ?? savedBlob.pathProgress
        let speedSegment = savedBlob.speedSegment ?? Math.floor(savedBlob.pathSegment / 2)

        if (speedSegment > 20) {
          speedSegment = speedSegment % 6
          speedProgress = savedBlob.speedProgress ?? 0
        }

        return {
          id: savedBlob.id,
          seed: savedBlob.seed,
          positionProgress: savedBlob.pathProgress,
          positionSegment: savedBlob.pathSegment,
          speedProgress,
          speedSegment,
          baseSpeed: savedBlob.speed,
          speedVariance: 0.5,
          currentSpeed: savedBlob.speed,
          size: savedBlob.size,
          color: savedBlob.color,
        }
      })
    }
  }, [savedBlobStates.length, dispatch])

  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0 || blobsRef.current.length === 0) {
      return
    }

    blobsRef.current.forEach((blob) => {
      if (!splineSystemsRef.current.has(blob.id)) {
        const splineSystem = createInfiniteBlobSplineSystem(blob.seed, {
          width: dimensions.width,
          height: dimensions.height,
        })
        splineSystemsRef.current.set(blob.id, splineSystem)
      }
    })
  }, [dimensions.width, dimensions.height])

  const initializeBlobs = useCallback(
    (width: number, height: number, blobConfigs: any[]) => {
      let blobs: Blob[] = []

      const dimensionThreshold = 0.3
      const widthDiff =
        lastDimensions.width > 0 ? Math.abs(width - lastDimensions.width) / width : 1
      const heightDiff =
        lastDimensions.height > 0 ? Math.abs(height - lastDimensions.height) / height : 1

      const hasValidSavedState =
        savedBlobStates.length > 0 &&
        widthDiff < dimensionThreshold &&
        heightDiff < dimensionThreshold &&
        savedBlobStates.every(
          (blob) => blob.size !== undefined && blob.size !== null && !isNaN(blob.size)
        )

      if (hasValidSavedState) {
        blobs = savedBlobStates.map((savedBlob) => {
          let speedProgress = savedBlob.speedProgress ?? savedBlob.pathProgress
          let speedSegment = savedBlob.speedSegment ?? Math.floor(savedBlob.pathSegment / 2)

          if (speedSegment > 20) {
            speedSegment = speedSegment % 6
            speedProgress = savedBlob.speedProgress ?? 0
          }

          return {
            id: savedBlob.id,
            seed: savedBlob.seed,
            positionProgress: savedBlob.pathProgress,
            positionSegment: savedBlob.pathSegment,
            speedProgress,
            speedSegment,
            baseSpeed: savedBlob.speed,
            speedVariance: 0.5,
            currentSpeed: savedBlob.speed,
            size: savedBlob.size,
            color: savedBlob.color,
          }
        })

        blobs.forEach((blob) => {
          const splineSystem = createInfiniteBlobSplineSystem(blob.seed, { width, height })
          splineSystemsRef.current.set(blob.id, splineSystem)
        })
      } else {
        if (savedBlobStates.length > 0) {
          dispatch(clearBlobStates())
        }
        const cookieState = getAnimatedBackgroundCookieState()

        if (cookieState && isCookieStateValid(cookieState)) {
          const blobCount = Math.min(blobConfigs.length, 5)
          const colorOptions = blobConfigs.map((config) => config?.color || '#FD9543')
          const elapsedTime = getElapsedTime(cookieState)

          for (let i = 0; i < blobCount; i++) {
            const blob = createSplineBlob(
              `blob-${i}`,
              { width, height },
              colorOptions,
              cookieState.masterSeed,
              elapsedTime
            )

            const config = blobConfigs[i % blobConfigs.length]
            if (config?.size) {
              blob.size = config.size + ((cookieState.masterSeed + i) % 40) - 20
            }

            blobs.push(blob)
            const splineSystem = createInfiniteBlobSplineSystem(blob.seed, { width, height })
            splineSystemsRef.current.set(blob.id, splineSystem)
          }

          dispatch(
            initializeWithCookieState({
              blobs: blobs.map((blob) => ({
                id: blob.id,
                seed: blob.seed,
                pathProgress: blob.positionProgress,
                pathSegment: blob.positionSegment,
                speed: blob.baseSpeed,
                size: blob.size,
                color: blob.color,
              })),
              dimensions: { width, height },
            })
          )
        } else {
          const blobCount = Math.min(blobConfigs.length, 5)
          const colorOptions = blobConfigs.map((config) => config?.color || '#FD9543')

          for (let i = 0; i < blobCount; i++) {
            const blob = createSplineBlob(`blob-${i}`, { width, height }, colorOptions)
            const config = blobConfigs[i % blobConfigs.length]
            if (config?.size) {
              blob.size = config.size + Math.random() * 40 - 20
            }

            blobs.push(blob)
            const splineSystem = createInfiniteBlobSplineSystem(blob.seed, { width, height })
            splineSystemsRef.current.set(blob.id, splineSystem)
          }
        }
      }

      blobsRef.current = blobs
    },
    [savedBlobStates, lastDimensions, dispatch]
  )

  const updateDimensions = useCallback(() => {
    if (typeof window === 'undefined') return

    const width = window.innerWidth
    const height = window.innerHeight

    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
    const isLowMemory = 'deviceMemory' in navigator && (navigator as any).deviceMemory <= 4
    const pixelRatio = window.devicePixelRatio || 1

    let scaleFactor = 1.0

    if (isLowMemory) {
      scaleFactor = 0.8
    }

    const effectivePixelRatio = Math.min(pixelRatio, isMobile ? 1.5 : 2.0)

    const newDimensions = {
      width: Math.floor((width * scaleFactor) / effectivePixelRatio),
      height: Math.floor((height * scaleFactor) / effectivePixelRatio),
    }

    // Calculate scale factor based on smaller dimension (reference: 900px for 1440p height)
    const minDimension = Math.min(newDimensions.width, newDimensions.height)
    const referenceSize = 1000
    scaleFactorRef.current = minDimension / referenceSize

    setDimensions(newDimensions)
    currentDimensionsRef.current = newDimensions
  }, [])

  // Initialize WebGL when component mounts
  useEffect(() => {
    const success = initWebGL()
    if (!success) {
    }
  }, [initWebGL])

  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return
    if (!colors || colors.length === 0) return
    if (blobsRef.current.length === 0) {
      initializeBlobs(dimensions.width, dimensions.height, colors)
    }
  }, [dimensions.width, dimensions.height, colors, initializeBlobs])

  useEffect(() => {
    updateDimensions()
    const handleResize = () => updateDimensions()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [updateDimensions])

  useEffect(() => {
    const saveInterval = setInterval(() => {
      if (blobsRef.current.length > 0 && dimensions.width > 0 && dimensions.height > 0) {
        dispatch(
          saveBlobStates({
            blobs: blobsRef.current.map((blob) => ({
              id: blob.id,
              seed: blob.seed,
              pathProgress: blob.positionProgress,
              pathSegment: blob.positionSegment,
              speedProgress: blob.speedProgress,
              speedSegment: blob.speedSegment,
              speed: blob.baseSpeed,
              size: blob.size,
              color: blob.color,
            })),
            dimensions: { width: dimensions.width, height: dimensions.height },
          })
        )
      }
    }, 2000)

    return () => {
      clearInterval(saveInterval)
      if (blobsRef.current.length > 0 && dimensions.width > 0 && dimensions.height > 0) {
        dispatch(
          saveBlobStates({
            blobs: blobsRef.current.map((blob) => ({
              id: blob.id,
              seed: blob.seed,
              pathProgress: blob.positionProgress,
              pathSegment: blob.positionSegment,
              speedProgress: blob.speedProgress,
              speedSegment: blob.speedSegment,
              speed: blob.baseSpeed,
              size: blob.size,
              color: blob.color,
            })),
            dimensions: { width: dimensions.width, height: dimensions.height },
          })
        )
      }
    }
  }, [dispatch, dimensions])

  useEffect(() => {
    if (!webglSupported) return

    const canvas = canvasRef.current
    const gl = glRef.current
    const program = programRef.current
    const blurProgram = blurProgramRef.current

    if (
      !canvas ||
      !gl ||
      !program ||
      !blurProgram ||
      dimensions.width === 0 ||
      dimensions.height === 0
    ) {
      return
    }

    const fb = framebufferRef.current
    const blurScale = blurScaleRef.current

    if (
      fb.width !== Math.max(1, Math.floor(dimensions.width * blurScale)) ||
      fb.height !== Math.max(1, Math.floor(dimensions.height * blurScale))
    ) {
      createFramebuffers(gl, dimensions.width, dimensions.height, blurScale)
    }

    const blurWidth = framebufferRef.current.width
    const blurHeight = framebufferRef.current.height

    let lastTime = animationTimeRef.current.lastTime
    let frameCount = animationTimeRef.current.frameCount
    let animationId: number

    const targetFPS = performanceMode === 'high' ? 60 : performanceMode === 'medium' ? 30 : 15
    const frameInterval = 1000 / targetFPS

    const positionLocation = gl.getAttribLocation(program, 'a_position')
    const resolutionLocation = gl.getUniformLocation(program, 'u_resolution')
    const blobCountLocation = gl.getUniformLocation(program, 'u_blobCount')
    const blobPositionsLocation = gl.getUniformLocation(program, 'u_blobPositions')
    const blobSizesLocation = gl.getUniformLocation(program, 'u_blobSizes')
    const blobColorsLocation = gl.getUniformLocation(program, 'u_blobColors')
    const blobOpacitiesLocation = gl.getUniformLocation(program, 'u_blobOpacities')

    const animate = (currentTime: number) => {
      const currentDimensions = currentDimensionsRef.current

      if (currentDimensions.width === 0 || currentDimensions.height === 0) {
        animationId = requestAnimationFrame(animate)
        return
      }

      frameCount++
      if (currentTime - lastTime < frameInterval) {
        animationId = requestAnimationFrame(animate)
        return
      }

      const deltaTime = currentTime - lastTime
      lastTime = currentTime

      animationTimeRef.current = { lastTime, frameCount }

      // Handle color transitions (read from refs to avoid stale closure)
      if (isTransitioningRef.current) {
        if (transitionStartTimeRef.current === null) {
          transitionStartTimeRef.current = currentTime
          transitionStartColorsRef.current = colorsRef.current.map((b) => b.color)
          console.log('[AnimatedBackgroundWebGL] Starting color transition:', {
            startColors: transitionStartColorsRef.current,
            targetColors: targetColorsRef.current.map((c) => c.color),
            currentTime,
          })
        }

        const transitionDuration = 500
        const elapsed = currentTime - transitionStartTimeRef.current
        const progress = Math.min(elapsed / transitionDuration, 1.0)
        const easedProgress = easeInOutCubic(progress)

        const interpolatedColors = colorsRef.current.map((blob, index) => {
          const startColor = transitionStartColorsRef.current[index] || blob.color
          const targetColor = targetColorsRef.current[index]?.color || blob.color
          return {
            ...blob,
            color: interpolateColor(startColor, targetColor, easedProgress),
          }
        })

        dispatch(updateColorsForTransition(interpolatedColors))

        // Update blob colors in refs
        blobsRef.current = blobsRef.current.map((blob, index) => ({
          ...blob,
          color: interpolatedColors[index]?.color || blob.color,
        }))

        if (progress >= 1.0) {
          console.log('[AnimatedBackgroundWebGL] Transition complete')
          dispatch(completeTransition())
          transitionStartTimeRef.current = null
          transitionStartColorsRef.current = []
        }
      }

      const blobs = blobsRef.current.map((blob) => {
        const splineSystem = splineSystemsRef.current.get(blob.id)
        if (!splineSystem) return blob

        // Scale speed with viewport to maintain consistent motion across resolutions
        const scaledSpeed = speed * 10 * scaleFactorRef.current
        return updateBlobProgressInfinite(blob, splineSystem, deltaTime, scaledSpeed)
      })

      blobsRef.current = blobs

      if (performance.now() % 2000 < 16) {
        dispatch(
          updateBlobStates(
            blobs.map((blob) => ({
              id: blob.id,
              seed: blob.seed,
              pathProgress: blob.positionProgress,
              pathSegment: blob.positionSegment,
              speed: blob.baseSpeed,
              size: blob.size,
              color: blob.color,
            }))
          )
        )
      }

      const targetBlur = targetBlurRef.current
      const currentBlur = currentBlurRef.current

      // Slower easing for smoother 20-second transition
      // At 60fps, this will take approximately 20 seconds to complete 95% of the transition
      const easingStrength = 0.005

      if (Math.abs(targetBlur - currentBlur) > 0.1) {
        const delta = targetBlur - currentBlur
        const easedDelta = delta * easingStrength
        currentBlurRef.current = currentBlur + easedDelta
      } else {
        currentBlurRef.current = targetBlur
      }

      const fb = framebufferRef.current
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb.framebuffer1)
      gl.viewport(0, 0, blurWidth, blurHeight)
      gl.clearColor(0, 0, 0, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)

      gl.useProgram(program)
      gl.uniform2f(resolutionLocation, currentDimensions.width, currentDimensions.height)

      const renderBlobs = blobs.map((blob) => {
        const splineSystem = splineSystemsRef.current.get(blob.id)
        if (!splineSystem) return { blob, position: { x: 0, y: 0 } }
        return { blob, position: getBlobPositionInfinite(blob, splineSystem) }
      })

      const blobCount = Math.min(renderBlobs.length, 10)

      if (debugMode) {
        const debugSplinePaths = new Map()
        const debugSpeedSplines = new Map()
        splineSystemsRef.current.forEach((system, blobId) => {
          const positionDebugInfo = system.positionSpline.getDebugInfo()
          const speedDebugInfo = system.speedSpline.getDebugInfo()
          debugSplinePaths.set(blobId, positionDebugInfo)
          debugSpeedSplines.set(blobId, speedDebugInfo)
        })

        const blobPositions = new Map()
        renderBlobs.forEach(({ blob, position }) => {
          blobPositions.set(blob.id, position)
        })

        animatedBackgroundDebugState.updateData({
          blobs,
          splinePaths: debugSplinePaths,
          speedSplines: debugSpeedSplines,
          blobPositions,
          dimensions: currentDimensions,
          version: 'webgl',
        })
      }

      if (blobCount === 0) {
        animationId = requestAnimationFrame(animate)
        return
      }

      const positions: number[] = []
      const sizes: number[] = []
      const colorValues: number[] = []
      const opacities: number[] = []

      for (let i = 0; i < 10; i++) {
        if (i < blobCount) {
          const { blob, position } = renderBlobs[i]
          positions.push(position.x, position.y)
          sizes.push(blob.size * scaleFactorRef.current)
          const [r, g, b] = hexToRgb(blob.color)
          colorValues.push(r, g, b)
          opacities.push(0.7)
        } else {
          positions.push(0, 0)
          sizes.push(0)
          colorValues.push(0, 0, 0)
          opacities.push(0)
        }
      }

      gl.uniform1i(blobCountLocation, blobCount)
      gl.uniform2fv(blobPositionsLocation, positions)
      gl.uniform1fv(blobSizesLocation, sizes)
      gl.uniform3fv(blobColorsLocation, colorValues)
      gl.uniform1fv(blobOpacitiesLocation, opacities)

      gl.bindBuffer(gl.ARRAY_BUFFER, bufferRef.current.position)
      gl.enableVertexAttribArray(positionLocation)
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0)

      gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0)

      gl.bindFramebuffer(gl.FRAMEBUFFER, fb.framebuffer2)
      gl.clear(gl.COLOR_BUFFER_BIT)

      gl.useProgram(blurProgram)
      const blurPositionLocation = gl.getAttribLocation(blurProgram, 'a_position')
      const blurTextureLocation = gl.getUniformLocation(blurProgram, 'u_texture')
      const blurResolutionLocation = gl.getUniformLocation(blurProgram, 'u_resolution')
      const blurDirectionLocation = gl.getUniformLocation(blurProgram, 'u_direction')
      const blurRadiusLocation = gl.getUniformLocation(blurProgram, 'u_blurRadius')

      gl.bindTexture(gl.TEXTURE_2D, fb.texture1)
      gl.uniform1i(blurTextureLocation, 0)
      gl.uniform2f(blurResolutionLocation, blurWidth, blurHeight)
      gl.uniform2f(blurDirectionLocation, 1.0, 0.0)
      gl.uniform1f(blurRadiusLocation, currentBlurRef.current)

      gl.bindBuffer(gl.ARRAY_BUFFER, bufferRef.current.position)
      gl.enableVertexAttribArray(blurPositionLocation)
      gl.vertexAttribPointer(blurPositionLocation, 2, gl.FLOAT, false, 0, 0)
      gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0)

      gl.bindFramebuffer(gl.FRAMEBUFFER, null)
      gl.viewport(0, 0, canvas.width, canvas.height)
      gl.clear(gl.COLOR_BUFFER_BIT)

      gl.bindTexture(gl.TEXTURE_2D, fb.texture2)
      gl.uniform1i(blurTextureLocation, 0)
      gl.uniform2f(blurResolutionLocation, blurWidth, blurHeight)
      gl.uniform2f(blurDirectionLocation, 0.0, 1.0)
      gl.uniform1f(blurRadiusLocation, currentBlurRef.current)

      gl.bindBuffer(gl.ARRAY_BUFFER, bufferRef.current.position)
      gl.enableVertexAttribArray(blurPositionLocation)
      gl.vertexAttribPointer(blurPositionLocation, 2, gl.FLOAT, false, 0, 0)
      gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0)

      animationId = requestAnimationFrame(animate)
    }

    animationId = requestAnimationFrame(animate)

    return () => {
      if (animationId) cancelAnimationFrame(animationId)
    }
  }, [webglSupported, speed, dispatch, dimensions, performanceMode, hexToRgb, createFramebuffers])

  if (!isEnabled || !webglSupported) {
    return null
  }

  if (performanceMode === 'disabled') {
    const scaleFactor = scaleFactorRef.current
    const size1 = Math.round(600 * scaleFactor)
    const size2 = Math.round(400 * scaleFactor)
    const size3 = Math.round(500 * scaleFactor)
    const blurAmount = Math.round(40 * scaleFactor)

    return (
      <div
        className="animated-background-static-bg"
        style={{
          width: '100vw',
          height: '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          zIndex: 1,
          background: `
            radial-gradient(${size1}px circle at 20% 30%, #FD954366 0%, transparent 50%),
            radial-gradient(${size2}px circle at 80% 70%, #F996CD66 0%, transparent 50%),
            radial-gradient(${size3}px circle at 40% 80%, #FF7B5A44 0%, transparent 50%)
          `,
          filter: `blur(${blurAmount}px)`,
          opacity: 0.7,
        }}
      />
    )
  }

  return (
    <canvas
      ref={canvasRef}
      width={dimensions.width}
      height={dimensions.height}
      className={`animated-background-canvas ${className}`}
      style={{
        width: '100vw',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 2,
        background: 'transparent',
      }}
    />
  )
}
